import React, { useEffect, useState } from "react";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import GoalButton from "layouts/goals/components/GoalButton";
import { getWeekdaysInRange, PUBLIC_HOLIDAYS_MESSAGES } from "./publicHolidays.constants";

function FieldLabel({ children }) {
  return (
    <SuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
      {children}
    </SuiTypography>
  );
}

function PublicHolidayFormModal({
  open,
  onClose,
  onSave,
  isSubmitting,
  initialHoliday,
  rangeModeEnabled,
}) {
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [localError, setLocalError] = useState(null);

  const isRangeMode = !initialHoliday && rangeModeEnabled;

  useEffect(() => {
    if (initialHoliday) {
      setName(initialHoliday.name || "");
      setStartDate(initialHoliday.date || "");
      setEndDate("");
    } else {
      setName("");
      setStartDate("");
      setEndDate("");
    }
    setLocalError(null);
  }, [initialHoliday, open]);

  const handleSaveClick = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setLocalError(PUBLIC_HOLIDAYS_MESSAGES.nameRequired);
      return;
    }

    // Range mode: collect weekdays between start and end
    if (isRangeMode) {
      const start = startDate?.trim();
      const end = endDate?.trim();

      if (!start || !end) {
        setLocalError(PUBLIC_HOLIDAYS_MESSAGES.rangeRequired);
        return;
      }

      const dates = getWeekdaysInRange(start, end);

      if (!dates.length) {
        setLocalError(PUBLIC_HOLIDAYS_MESSAGES.noWeekdaysInRange);
        return;
      }

      await onSave({ name: trimmedName, dates });
      return;
    }

    // Single-day (edit or single create)
    const singleDate = startDate?.trim();
    if (!singleDate) {
      setLocalError(PUBLIC_HOLIDAYS_MESSAGES.dateRequired);
      return;
    }

    await onSave({ name: trimmedName, dates: [singleDate] });
  };

  const weekdayCount = isRangeMode ? getWeekdaysInRange(startDate, endDate).length : 0;

  return (
    <Dialog open={open} onClose={() => !isSubmitting && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>{initialHoliday ? "Edit public holiday" : "Add public holiday"}</DialogTitle>
      <DialogContent>
        {localError && (
          <SuiTypography variant="caption" color="error" display="block" mb={1}>
            {localError}
          </SuiTypography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FieldLabel>Holiday name</FieldLabel>
            <SuiInput
              placeholder="e.g. Eid ul Fitr"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          {isRangeMode ? (
            <>
              <Grid item xs={12} sm={6}>
                <FieldLabel>Start date</FieldLabel>
                <SuiInput
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldLabel>End date</FieldLabel>
                <SuiInput
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <SuiTypography variant="caption" color="text" display="block">
                  {weekdayCount
                    ? `${weekdayCount} weekday${weekdayCount > 1 ? "s" : ""} will be added. `
                    : ""}
                  Weekends are skipped automatically.
                </SuiTypography>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <FieldLabel>Date</FieldLabel>
              <SuiInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </GoalButton>
        <GoalButton
          variant="primary"
          icon="save"
          loading={isSubmitting}
          onClick={handleSaveClick}
        >
          {initialHoliday ? "Save changes" : "Add holiday"}
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default PublicHolidayFormModal;
